import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { Search } from '@videoblocks/react-icons';

import { selectAuthFeatureFlags } from '../../auth/AuthSelectors';
import {
  GLOW_PINK,
  GLOW_PURPLE,
} from '../Search/components/ProminentSearch/ConversationalSearchElements/AutoOnHoverTrace';
import { getConversationalSearchUnavailableMessage } from '../Search/components/ProminentSearch/ConversationalSearchElements/conversationalSearchMessaging';
import {
  SPARKLE_ICON_PATHS,
  SPARKLE_ICON_VIEWBOX,
} from '../Search/components/ProminentSearch/ConversationalSearchElements/sparkleIcon';
import { NavSearchContext } from '../Search/components/navSearch/NavSearchContext';

// Kept separate from the search page's SearchWithAIToggle: this is the modern
// segmented design (per the Conversational Search Figma) and the intended
// consolidation target — once the search page adopts it, promote this
// component to ConversationalSearchElements and delete SearchWithAIToggle.
//
// Unlike the search page, this toggle is click-only by requirement: no
// auto-enable-on-hover and no tooltips in the top nav.

// Inline background rather than an SVG gradient fill: a url(#id) fill can be
// captured by another mounted instance's <defs>, an inline background cannot.
const AI_GRADIENT = `linear-gradient(122deg, ${GLOW_PURPLE} 0%, #cc5ce3 50%, ${GLOW_PINK} 100%)`;

// Arbitrary Tailwind values are unavailable (v2, no JIT), so exact px sizes and
// the hover glow are set with inline styles instead of bracket classes.
const ICON_SIZE_PX = 13;
const HOVER_GLOW = '0 0 12px 2px rgba(204, 92, 227, 0.5)';

const SEGMENT_CLS =
  'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-200';

// How far the active indicator travels between the two segments: one 32px
// (w-8) segment plus the 2px (gap-0.5) gap. Inline transform rather than a
// translate-x-* utility for the same Tailwind v2/v4 conflict the search
// page's sliding pill dodges.
const ACTIVE_SLIDE_OFFSET_PX = 34;

interface TopNavSearchToggleProps {
  disabledForContentType?: boolean;
}

function SparkleIcon({ className }: { className?: string }): JSX.Element {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={SPARKLE_ICON_VIEWBOX}
      width={ICON_SIZE_PX}
      height={ICON_SIZE_PX}
      className={className}
      aria-hidden="true"
    >
      {SPARKLE_ICON_PATHS.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  );
}

export default function TopNavSearchToggle({
  disabledForContentType = false,
}: TopNavSearchToggleProps): JSX.Element {
  const {
    conversationalSearchEnabled,
    setConversationalSearchEnabled: setEnabled,
  } = useContext(NavSearchContext);
  const { showConversationalSearchAudio } = useSelector(selectAuthFeatureFlags);

  const [hovered, setHovered] = useState(false);

  // The disabled branch renders without mouse handlers, so a mid-hover switch
  // to an unsupported content type would leave the glow stuck on for the next
  // enabled render — reset it here.
  useEffect(() => {
    if (disabledForContentType) {
      setHovered(false);
    }
  }, [disabledForContentType]);

  if (disabledForContentType) {
    return (
      <button
        type="submit"
        data-testid="topnav-search-toggle"
        data-mode="no-ai"
        aria-label="Search"
        title={getConversationalSearchUnavailableMessage(
          showConversationalSearchAudio ?? false
        )}
        className={twMerge(
          SEGMENT_CLS,
          'cursor-pointer bg-gray-800 hover:bg-gray-900'
        )}
      >
        <Search
          className="fill-white"
          style={{ width: ICON_SIZE_PX, height: ICON_SIZE_PX }}
        />
      </button>
    );
  }

  const handleToggle = () => {
    setEnabled((prev) => !prev);
    void Telemetry.increment('top_nav_conversational_search_toggle_click');
  };

  return (
    <button
      type="button"
      data-testid="topnav-search-toggle"
      data-mode={conversationalSearchEnabled ? 'ai' : 'standard'}
      aria-label="Toggle AI search"
      aria-pressed={conversationalSearchEnabled}
      onClick={handleToggle}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{ boxShadow: hovered ? HOVER_GLOW : undefined }}
      className="relative flex h-10 flex-shrink-0 cursor-pointer items-center gap-0.5 rounded-full bg-gray-110 p-1 transition-shadow duration-200"
    >
      {/* Active indicator slides between the segments (mirroring the search
          page's sliding pill); its gray/gradient layers crossfade because
          background-image itself cannot be transitioned. */}
      <span
        aria-hidden="true"
        data-testid="topnav-search-active"
        className="absolute left-1 top-1 h-8 w-8 transition-transform duration-200 ease-in-out"
        style={{
          transform: conversationalSearchEnabled
            ? `translateX(${ACTIVE_SLIDE_OFFSET_PX}px)`
            : 'translateX(0)',
        }}
      >
        <span
          data-testid="topnav-search-active-standard"
          className={twMerge(
            'absolute inset-0 rounded-full bg-gray-800 transition-opacity duration-200',
            conversationalSearchEnabled ? 'opacity-0' : 'opacity-100'
          )}
        />
        <span
          data-testid="topnav-search-active-ai"
          className={twMerge(
            'absolute inset-0 rounded-full transition-opacity duration-200',
            conversationalSearchEnabled ? 'opacity-100' : 'opacity-0'
          )}
          style={{ backgroundImage: AI_GRADIENT }}
        />
      </span>
      <span
        data-testid="topnav-search-standard"
        className={twMerge(SEGMENT_CLS, 'relative')}
      >
        <Search
          className={
            conversationalSearchEnabled ? 'fill-gray-550' : 'fill-white'
          }
          style={{ width: ICON_SIZE_PX, height: ICON_SIZE_PX }}
        />
      </span>
      <span
        data-testid="topnav-search-ai"
        className={twMerge(SEGMENT_CLS, 'relative')}
      >
        <SparkleIcon
          className={
            conversationalSearchEnabled ? 'fill-white' : 'fill-gray-550'
          }
        />
      </span>
    </button>
  );
}
